import { action, computed, makeObservable, observable, ObservableMap, ObservableSet } from "mobx";
import { localBlobStoreModule } from "@/modules/local-blob-store";
import { LocalBlobIndexEntry } from "@/modules/local-blob-store/index/local-blob-index-entry";
import { LocalBlobIndexRepository } from "@/modules/local-blob-store/index/local-blob-index-repository";
import {
  AttachmentId,
  LocalBlobStoreApi,
  PutPendingResult,
} from "@/modules/local-blob-store/types";
import { logger } from "@/modules/logger";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";

/**
 * Public API for the on-device blob cache. Keeps the byte store and its index in
 * sync on every write, and hands out `blob:` URLs for rendering while owning
 * their lifecycle so they don't leak — an unrevoked object URL pins its blob in
 * memory.
 *
 * Reads are cache-aside: a miss tells the caller to fetch the image and cache it
 * back. Blobs created offline are the cache's only copy until they upload, so
 * they're protected from eviction; re-fetchable ones are not.
 */
export class AppStoreLocalBlobStoreStore extends AppSubStore {
  /** Hard ceiling on the cache budget, regardless of how large the quota is. */
  private static readonly MAX_BUDGET_BYTES = 500_000_000;

  /** One minted object URL per id. ObservableMap so minting re-fires the render reaction. */
  private readonly objectUrls = new ObservableMap<AttachmentId, string>();

  /**
   * Attachment ids with a local upload to GCS in flight. Observable so the
   * render resolver can distinguish a PENDING *upload* (row 3, "Uploading…"
   * badge) from a PENDING *external* paste (row 4, shimmer). Set on upload
   * start, cleared on success/failure.
   */
  private readonly uploadsInFlight = new ObservableSet<AttachmentId>();

  /**
   * Ids with a `pending` index entry, mirrored as an in-memory observable set so
   * the render resolver can answer "is this a local upload?" synchronously (the
   * index read is async). Unlike `uploadsInFlight`, this is durable across
   * reload: it's rehydrated from the index in `init()`, so a refreshed
   * offline-created image is still recognized as a local upload (and shows the
   * "Uploading…" badge / blob, not the external shimmer). Maintained on every
   * pending↔uploaded transition and on delete.
   */
  private readonly pendingIds = new ObservableSet<AttachmentId>();

  public mintedCount = 0;
  public revokedCount = 0;

  /**
   * Forces the budget to a fixed size, bypassing the quota-derived computation.
   * Null means "use the computed budget". The overlay sets this to shrink the
   * budget and watch LRU eviction happen live; tests set it to drive eviction
   * deterministically. Not for production use.
   */
  public budgetOverride: number | null = null;

  /** The OPFS byte store, overridable in tests where the Worker can't run. */
  private readonly byteStore: LocalBlobStoreApi;

  constructor(args: AppSubStoreArgs, byteStore: LocalBlobStoreApi = localBlobStoreModule) {
    super(args);
    this.byteStore = byteStore;
    makeObservable<
      this,
      | "objectUrls"
      | "uploadsInFlight"
      | "pendingIds"
      | "byteStore"
      | "index"
      | "put"
      | "touch"
      | "mintObjectUrl"
      | "revokeObjectUrl"
      | "deleteEntry"
      | "rehydratePendingIds"
    >(this, {
      objectUrls: observable,
      uploadsInFlight: observable,
      pendingIds: observable,
      mintedCount: observable,
      revokedCount: observable,
      budgetOverride: observable,
      markUploadInFlight: action,
      clearUploadInFlight: action,
      isUploadInFlight: false,
      isPending: false,
      isLocalUpload: false,
      liveObjectUrlCount: computed,
      byteStore: false,
      index: false,
      getBlobUrl: false,
      getCachedObjectUrl: false,
      putPending: false,
      putCached: false,
      markUploaded: false,
      listPending: false,
      evict: false,
      clearAll: false,
      clearUploaded: false,
      init: false,
      currentBudget: false,
      setBudgetOverride: false,
      enforceBudget: false,
      reconcile: false,
      rehydratePendingIds: false,
      put: false,
      touch: false,
      mintObjectUrl: false,
      revokeObjectUrl: false,
      deleteEntry: false,
      teardown: false,
    });
  }

  private get index(): LocalBlobIndexRepository {
    return this.store.memDb.localBlobIndex;
  }

  public get liveObjectUrlCount(): number {
    return this.objectUrls.size;
  }

  /**
   * The cached `blob:` URL for `attachmentId`, or null on a miss. Touches
   * `lastAccessAt`. Returns null without touching the index if the row exists
   * but the bytes are gone (torn state) — this is a pure read.
   */
  async getBlobUrl(attachmentId: AttachmentId): Promise<string | null> {
    const entry = await this.index.get(attachmentId);
    if (entry === undefined) {
      return null;
    }

    const existing = this.objectUrls.get(attachmentId);
    if (existing !== undefined) {
      await this.touch(entry);
      return existing;
    }

    const bytes = await this.byteStore.readBlob(attachmentId);
    if (bytes === null) {
      return null;
    }

    const url = this.mintObjectUrl(attachmentId, bytes, entry.mimeType);
    await this.touch(entry);
    return url;
  }

  /**
   * The already-minted `blob:` URL for `attachmentId`, or null if none has been
   * minted yet. Synchronous and observable — safe to read from a MobX reaction
   * or render. A null result does NOT mean "not cached"; it means "no object URL
   * in memory". Call `getBlobUrl` (async) to mint one from OPFS bytes on a miss.
   */
  getCachedObjectUrl(attachmentId: AttachmentId): string | null {
    return this.objectUrls.get(attachmentId) ?? null;
  }

  /** Mark a local upload as in flight (drives the "Uploading…" badge). */
  markUploadInFlight(attachmentId: AttachmentId): void {
    this.uploadsInFlight.add(attachmentId);
  }

  /** Clear the in-flight marker once the upload succeeds or fails. */
  clearUploadInFlight(attachmentId: AttachmentId): void {
    this.uploadsInFlight.delete(attachmentId);
  }

  /** Observable: is a local upload for this id in flight? */
  isUploadInFlight(attachmentId: AttachmentId): boolean {
    return this.uploadsInFlight.has(attachmentId);
  }

  /**
   * Observable: does this id have a `pending` blob (a local upload not yet in
   * GCS)? Durable across reload (rehydrated in `init`), unlike the in-memory
   * `uploadsInFlight` marker. An input to `isLocalUpload`.
   */
  isPending(attachmentId: AttachmentId): boolean {
    return this.pendingIds.has(attachmentId);
  }

  /**
   * Observable: is this id a local upload (vs. an external paste)? The single
   * signal the render resolver uses to choose the "Uploading…" badge / blob over
   * the external shimmer for a PENDING image. True while the upload is in flight
   * this session, OR whenever a `pending` blob exists — the latter survives
   * reload, so a refreshed offline-created image is still recognized.
   */
  isLocalUpload(attachmentId: AttachmentId): boolean {
    return this.isUploadInFlight(attachmentId) || this.isPending(attachmentId);
  }

  /**
   * Cache an offline-created blob that is the only copy until it uploads.
   *
   * Pending bytes are never evictable, so — unlike `putCached` — a full cache
   * can't make room by evicting. Guard before writing: if the new bytes would
   * push the cache over budget, refuse with a typed `cache-full` result rather
   * than throw `QuotaExceededError` and silently lose the only copy. Read the
   * size before `put` because `writeBlob` transfers (detaches) the buffer.
   */
  async putPending(
    attachmentId: AttachmentId,
    bytes: ArrayBuffer,
    mimeType: string
  ): Promise<PutPendingResult> {
    const sizeBytes = bytes.byteLength;
    const budget = await this.currentBudget();
    const projectedTotal = (await this.index.getTotalSizeBytes()) + sizeBytes;
    if (projectedTotal > budget) {
      logger.warn({
        message: "[AppStoreLocalBlobStoreStore] putPending refused: cache full",
        info: { attachmentId, projectedTotal, budget },
      });
      return { ok: false, reason: "cache-full" };
    }

    await this.put(attachmentId, bytes, mimeType, "pending");
    this.pendingIds.add(attachmentId);
    return { ok: true };
  }

  /** Cache a re-fetchable blob (cache-on-view / cache-on-insert). */
  async putCached(attachmentId: AttachmentId, bytes: ArrayBuffer, mimeType: string): Promise<void> {
    await this.put(attachmentId, bytes, mimeType, "uploaded");
    await this.enforceBudget();
  }

  /**
   * Mark a blob uploaded so it becomes evictable. No-op if not pending.
   *
   * The entry was eviction-immune while pending (and `putPending` skips
   * `enforceBudget`), so flipping it to `uploaded` can leave the cache over
   * budget — enforce here, the way `putCached` does on write.
   */
  async markUploaded(attachmentId: AttachmentId): Promise<void> {
    const entry = await this.index.get(attachmentId);
    if (entry === undefined || entry.uploadState === "uploaded") {
      return;
    }
    await this.index.upsert({ ...entry, uploadState: "uploaded" });
    this.pendingIds.delete(attachmentId);
    await this.enforceBudget();
  }

  /** Every pending entry, for the reconnect upload drain. */
  async listPending(): Promise<LocalBlobIndexEntry[]> {
    return this.index.listPending();
  }

  /** Delete a blob's bytes, index row, and object URL. */
  async evict(attachmentId: AttachmentId): Promise<void> {
    await this.deleteEntry(attachmentId);
  }

  /**
   * One-time startup work: request persistent storage (best-effort exemption
   * from the browser's own eviction), heal index/OPFS divergence, then trim the
   * cache to budget. Safe to call without awaiting the result.
   */
  async init(): Promise<void> {
    try {
      await navigator.storage?.persist?.();
      await this.rehydratePendingIds();
      await this.reconcile();
      await this.enforceBudget();
    } catch (error) {
      // Best-effort: a startup that can't persist/reconcile/enforce must not
      // throw — a fire-and-forget `void init()` would surface as an unhandled
      // rejection. The cache still works; it's just not pruned this boot.
      logger.warn({
        message: "[AppStoreLocalBlobStoreStore] init failed",
        info: { error: String(error) },
      });
    }
  }

  /**
   * The cache budget in bytes: `min(500MB, 50% of quota)`, read from
   * `navigator.storage.estimate()` at runtime so it tracks the engine's actual
   * quota. `budgetOverride`, when set, wins — for the overlay and tests only.
   */
  async currentBudget(): Promise<number> {
    if (this.budgetOverride !== null) {
      return this.budgetOverride;
    }
    const { quota } = (await navigator.storage?.estimate?.()) ?? {};
    // No usable quota (e.g. estimate() unavailable) → fall back to the ceiling
    // rather than a 0 budget that would evict the whole cache.
    if (quota === undefined || quota <= 0) {
      return AppStoreLocalBlobStoreStore.MAX_BUDGET_BYTES;
    }
    return Math.min(AppStoreLocalBlobStoreStore.MAX_BUDGET_BYTES, quota * 0.5);
  }

  /** Force the budget to a fixed size (overlay / tests). Null restores auto. */
  setBudgetOverride(bytes: number | null): void {
    this.budgetOverride = bytes;
  }

  /**
   * Trim the cache to budget by LRU-evicting `uploaded` entries — oldest
   * `lastAccessAt` first — until total bytes are under budget. `pending` entries
   * are never evicted: they are the only copy of un-uploaded blobs, so dropping
   * them is data loss. Evicted `uploaded` blobs re-fetch on next view, so this
   * is recoverable by design. Runs after each `putCached` and on init.
   */
  async enforceBudget(): Promise<void> {
    const budget = await this.currentBudget();
    let total = await this.index.getTotalSizeBytes();
    if (total <= budget) {
      return;
    }

    // Oldest-first; pending excluded by the query, so it's never a victim.
    const victims = await this.index.listUploadedByLastAccessAsc();
    for (const victim of victims) {
      if (total <= budget) {
        break;
      }
      await this.deleteEntry(victim.attachmentId);
      total -= victim.sizeBytes;
    }
  }

  /**
   * Heal divergence between the index and OPFS: an `uploaded` index row whose
   * bytes are gone is dropped (treat as a miss — it re-fetches on next view); an
   * OPFS file with no index row is an orphan and gets GC'd. Run on init.
   *
   * A `pending` row whose bytes are gone is NEVER dropped — it's the only record
   * that an offline-created blob still needs uploading, so dropping it is data
   * loss (the same invariant `enforceBudget` upholds). The row stays as a torn
   * entry for the pending-drain path (M3) to deal with.
   */
  async reconcile(): Promise<void> {
    const [files, rows] = await Promise.all([this.byteStore.listBlobs(), this.index.listAll()]);
    const fileIds = new Set(files.map(file => file.id));
    const rowIds = new Set(rows.map(row => row.attachmentId));

    for (const row of rows) {
      if (row.uploadState === "pending") {
        continue;
      }
      if (!fileIds.has(row.attachmentId)) {
        this.revokeObjectUrl(row.attachmentId);
        await this.index.delete(row.attachmentId);
      }
    }
    for (const file of files) {
      if (!rowIds.has(file.id)) {
        await this.byteStore.deleteBlob(file.id);
      }
    }
  }

  /**
   * Rebuild the in-memory `pendingIds` mirror from the index. Pending entries
   * survive reload (and the in-memory set does not), so init replays them so the
   * resolver recognizes offline-created images as local uploads after a refresh.
   */
  private async rehydratePendingIds(): Promise<void> {
    const pending = await this.index.listPending();
    for (const entry of pending) {
      this.pendingIds.add(entry.attachmentId);
    }
  }

  /** Revoke every live object URL. Call on teardown to avoid leaking blobs. */
  teardown(): void {
    for (const attachmentId of [...this.objectUrls.keys()]) {
      this.revokeObjectUrl(attachmentId);
    }
  }

  /**
   * Drop the entire cache — bytes, index rows, and object URLs, including
   * pending. For logout: nothing of the signed-out account may survive on the
   * device for the next user.
   */
  async clearAll(): Promise<void> {
    this.teardown();
    this.pendingIds.clear();
    await this.byteStore.deleteAllBlobs();
    await this.index.clearAll();
  }

  /** Drop uploaded blobs, preserving pending ones (the only copy of offline bytes). */
  async clearUploaded(): Promise<void> {
    const uploaded = await this.index.listUploadedByLastAccessAsc();
    await Promise.allSettled(uploaded.map(({ attachmentId }) => this.evict(attachmentId)));
  }

  private async put(
    attachmentId: AttachmentId,
    bytes: ArrayBuffer,
    mimeType: string,
    uploadState: LocalBlobIndexEntry["uploadState"]
  ): Promise<void> {
    // Fresh bytes invalidate any URL minted from the old ones.
    this.revokeObjectUrl(attachmentId);

    // Read the size BEFORE writing: `writeBlob` transfers the buffer to the
    // Worker (zero-copy), which detaches it here and zeroes its `byteLength`.
    const sizeBytes = bytes.byteLength;
    await this.byteStore.writeBlob(attachmentId, bytes);
    await this.index.upsert({
      attachmentId,
      mimeType,
      sizeBytes,
      uploadState,
      lastAccessAt: Date.now(),
    });
  }

  /** Delete a blob's bytes, index row, and object URL. The eviction primitive. */
  private async deleteEntry(attachmentId: AttachmentId): Promise<void> {
    this.revokeObjectUrl(attachmentId);
    this.pendingIds.delete(attachmentId);
    await this.byteStore.deleteBlob(attachmentId);
    await this.index.delete(attachmentId);
  }

  private async touch(entry: LocalBlobIndexEntry): Promise<void> {
    // Field-scoped, not a full-row put of the (possibly stale) snapshot, so a
    // concurrent markUploaded flip isn't clobbered back to pending.
    await this.index.updateLastAccessAt(entry.attachmentId, Date.now());
  }

  private mintObjectUrl(attachmentId: AttachmentId, bytes: ArrayBuffer, mimeType: string): string {
    const existing = this.objectUrls.get(attachmentId);
    if (existing !== undefined) {
      return existing;
    }

    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    this.objectUrls.set(attachmentId, url);
    this.mintedCount += 1;
    return url;
  }

  private revokeObjectUrl(attachmentId: AttachmentId): void {
    const url = this.objectUrls.get(attachmentId);
    if (url === undefined) {
      return;
    }
    try {
      URL.revokeObjectURL(url);
    } catch {
      logger.warn({
        message: "[AppStoreLocalBlobStoreStore] revokeObjectURL failed",
        info: { attachmentId },
      });
    }
    this.objectUrls.delete(attachmentId);
    this.revokedCount += 1;
  }
}
